// Copyright (c) Ping. All rights reserved.
//
// Web host for `removeMembersFromGroupConversation` — the reverse of
// `addWebGroupMembers.ts`. Issues the MLS Commit that evicts the selected
// accounts, then patches the local Dexie roster so the Members list + member
// count update immediately. A `com.ping.member` leave event is announced to the
// surviving group so peers refresh their rosters too.

import {
  encodeEnvelopeJsonBytes,
  type MessagingClientLike,
  removeConversationMembersOnServer,
  type RemoveGroupMembersResult,
  removeMembersFromGroupConversation,
} from '@ping/ui';
import { buildGroupConversationMetaName } from '@ping/ui/src/conversation/groupConversation';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { isEpochConflictError } from './addWebGroupMembers';
import { runSerializedCommit, syncBeforeCommit } from './groupCommitQueue';
import { getOrCreateMlsConversation } from './mlsConversation';
import { persistLocalRoomEvent } from './persistLocalRoomEvent';

export type { RemoveGroupMembersResult };
export { GroupCreateError } from '@ping/ui';

export interface RemoveWebGroupMembersParams {
  readonly client: MessagingClientLike;
  readonly conversationId: string;
  readonly removeAccountIds: readonly string[];
}

/**
 * Evict members from a group (MLS Commit) + merge the local Dexie roster.
 *
 * Serialized per conversation + synced-before-commit + bounded resync-retry, same
 * as `addWebGroupMembers`, so a removal Commit can't desync the group epoch.
 */
export async function removeWebGroupMembers(
  params: RemoveWebGroupMembersParams,
): Promise<RemoveGroupMembersResult> {
  return runSerializedCommit(params.conversationId, () => removeWebGroupMembersInner(params));
}

async function removeWebGroupMembersInner(
  params: RemoveWebGroupMembersParams,
): Promise<RemoveGroupMembersResult> {
  const identity = await retrieveIdentity();
  if (identity === null) {
    throw new Error('Still signing you in. Try again in a moment.');
  }

  const existing = await db.conversations.get(params.conversationId);
  if (existing?.kind !== 'group') {
    throw new Error('This chat is not a group.');
  }

  // Fast-forward to the server's latest epoch before crafting the removal Commit.
  await syncBeforeCommit(params.client);

  // Bounded resync-retry on an epoch conflict (concurrent committer).
  const MAX_ATTEMPTS = 2;
  let result: RemoveGroupMembersResult | undefined;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    // Make sure the SDK actually knows about this group before we ask it for the
    // roster / issue the removal Commit (mirrors addWebGroupMembers).
    await getOrCreateMlsConversation({
      client: params.client,
      transport: getCurrentTransport(),
      beConversationId: params.conversationId,
      conversationName: existing.name,
      log,
    });

    try {
      result = await removeMembersFromGroupConversation({
        client: params.client,
        conversationIdHex: params.conversationId,
        removeAccountIds: params.removeAccountIds,
        log,
      });
      break;
    } catch (err) {
      if (!isEpochConflictError(err) || attempt === MAX_ATTEMPTS - 1) throw err;
      log.warn({
        event: 'webGroup.removeMembers.epochConflict.resync',
        attempt: attempt + 1,
        conversationIdPrefix: params.conversationId.slice(0, 8),
      });
      await syncBeforeCommit(params.client);
    }
  }
  if (result === undefined) {
    throw new Error("Couldn't remove those members. Try again.");
  }

  const removed = new Set(result.removedAccountIds);
  const nextAccountIds = (existing.memberAccountIds ?? []).filter((id) => !removed.has(id));
  const nextDisplayNames: Record<string, string> = { ...(existing.memberDisplayNames ?? {}) };
  const nextRoles: Record<string, 'owner' | 'admin' | 'member'> = {
    ...(existing.memberRoles ?? {}),
  };
  for (const id of removed) {
    delete nextDisplayNames[id];
    delete nextRoles[id];
  }

  // Remember the departures so THIS device's own authoritative server-roster
  // reconcile (memberProfileHydration) can't RE-ADD the removed members before
  // the server records the removal — otherwise the admin watches their own
  // removal undo itself on the next hydrate pass (the admin's kick is echoed via
  // the messages store, bypassing the `onMessage` kick handler that writes
  // `departedAccountIds` for other members).
  const nextDeparted = [...new Set([...(existing.departedAccountIds ?? []), ...removed])];

  await db.conversations.update(params.conversationId, {
    memberAccountIds: nextAccountIds,
    memberDisplayNames: nextDisplayNames,
    memberRoles: nextRoles,
    departedAccountIds: nextDeparted,
  });

  // SERVER-AUTHORITATIVE REMOVAL. The MLS Remove Commit is opaque to the backend,
  // so the server roster (`GET /v1/conversations/{id}/members`) keeps listing the
  // removed accounts as active until we tell it. Without this, every member's
  // authoritative reconcile re-adds the removed member and message-service keeps
  // fanning traffic to their (now-undecryptable) devices. Best-effort — the
  // `com.ping.kick` broadcast + `departedAccountIds` cover the window until the
  // server catches up; a failure here is retried by the next reconcile.
  void removeConversationMembersOnServer(params.conversationId, [...removed]).catch(
    (err: unknown) =>
      log.warn({
        event: 'webGroup.removeMembers.serverRemove.failed',
        conversationIdPrefix: params.conversationId.slice(0, 8),
        error: (err as Error).message,
      }),
  );

  // AUTHORITATIVE META REWRITE (mirror of the add path). Rewrite the MLS
  // GroupContext meta with the REDUCED roster so a member who joins later parses
  // a roster that already excludes the removed accounts — otherwise the meta
  // still lists them and a late joiner would re-add them to their local roster.
  // Best-effort (epoch-advancing self-update commit); the removal already
  // succeeded, and the `com.ping.kick` broadcast + B4 server reconcile are the
  // backstops for surviving members.
  if (typeof result.conv.setConversationName === 'function') {
    try {
      const metaName = buildGroupConversationMetaName({
        inviter: { accountId: identity.accountId, displayName: identity.displayName },
        members: nextAccountIds.map((id) => ({
          accountId: id,
          displayName: nextDisplayNames[id] ?? '',
        })),
        ...(existing.name !== undefined && existing.name.trim().length > 0
          ? { title: existing.name.trim() }
          : {}),
        ...(existing.avatarMediaId !== undefined && existing.avatarMediaId.length > 0
          ? { avatarMediaId: existing.avatarMediaId }
          : {}),
      });
      await result.conv.setConversationName(metaName);
      log.info({
        event: 'webGroup.removeMembers.metaRewritten',
        conversationIdPrefix: params.conversationId.slice(0, 8),
        rosterSize: nextAccountIds.length,
      });
    } catch (err) {
      log.warn({
        event: 'webGroup.removeMembers.metaRewrite.failed',
        conversationIdPrefix: params.conversationId.slice(0, 8),
        error: (err as Error).message,
      });
    }
  }

  // Announce the removal so surviving peers refresh their roster UIs (the MLS
  // Commit already evicted the leaves cryptographically; this is the app-level
  // `com.ping.kick` mirror so the Members list reflects it without a resync).
  for (const accountId of removed) {
    try {
      const kickEnvelope = {
        kind: 'com.ping.kick' as const,
        payload: {
          target_user_id: accountId,
          reason: 'removed_by_admin',
          actorDisplayName: identity.displayName,
          targetDisplayName: existing.memberDisplayNames?.[accountId] ?? '',
        },
      };
      const sent = await result.conv.send(encodeEnvelopeJsonBytes(kickEnvelope));
      // Local echo so the remover (actor) sees the "removed X" event bubble too.
      await persistLocalRoomEvent({
        conversationId: params.conversationId,
        senderAccountId: identity.accountId,
        envelope: kickEnvelope,
        contentHash: sent.content_hash,
      });
    } catch (err) {
      log.warn({ event: 'webGroup.removeEvent.failed', error: (err as Error).message });
    }
  }

  log.info({
    event: 'webGroup.removeMembers.ok',
    conversationIdPrefix: params.conversationId.slice(0, 8),
    removedCount: result.removedAccountIds.length,
  });

  return result;
}
